import { UtilityService } from '@eforall/common';
import { AccScheduleAvailableSeatCount, Commitment, SelectedSlots, SLOTS_DAY_MAP, WeekdayIndex } from '@interfaces';

const LMI = 60;
const BIPOC = 40;
const MAYBE_BIPOC = 4;
const FEMALE = 15;
const MAYBE_NONMALE = 1.5;

const MATCH_DIFFICULTY_LOW = 0;
const MATCH_DIFFICULTY_NORMAL = 100;
const MATCH_DIFFICULTY_HIGH = 500;

const IMPACT_CRITICAL = 10000;
const IMPACT_IMPORTANT = 3000;
const IMPACT_STANDARD = 1000;
const IMPACT_INTERNAL = 400;

// Discriminated union for input items
type LeadOrAccApplicationData =
	{
		type: 'lead',
		data: {
			accLeadId: number,
			demographics: string,
			mayBeDemographics: string,
			selectedMeetingSlots?: SelectedSlots,
		}[],
		alignedAccs: { accLeadId: number, accId: number }[],
		alignedCommitments: { accLeadId: number, commitmentId: number }[],
	}
	|
	{
		type: 'accApplication',
		data: {
			accApplicationId: number,
			demographics: string,
			mayBeDemographics: string,
			selectedMeetingSlots?: SelectedSlots,
		}[],
		alignedAccs: { accApplicationId: number, accId: number }[],
		alignedCommitments: { accApplicationId: number, commitmentId: number }[],

	};

/**
 * Calculates sorting scores for leads or accelerator applications and returns a map of id to score.
 * Normalizes both types to a common structure and processes in a single loop.
 * 
 * @param util UtilityService instance for date formatting and helpers.
 * @param items Discriminated union containing either leads or acc applications and their aligned data.
 * @param commitmentsWithScore Array of commitments with calculated scores.
 * @param accScheduleAndAvailableSeatCount Map of accId to schedule and available seat count.
 * @returns Object mapping id (accLeadId or accApplicationId) to sortingScore.
 */
export function calculateLeadsOrAccApplicationsScore(
	util: UtilityService,
	items: LeadOrAccApplicationData,
	commitmentsWithScore: Commitment[],
	accScheduleAndAvailableSeatCount: { [accId: number]: AccScheduleAvailableSeatCount },
): { [id: number]: number } {
	const scoreMap: { [id: number]: number } = [];

	// Normalize all items to a common structure
	const normalized: {
		id: number,
		demographics: string,
		mayBeDemographics: string,
		selectedMeetingSlots?: SelectedSlots,
		alignedAccIds: number[],
		alignedCommitmentIds: number[]
	}[] = [];

	if (items.type === 'lead') {
		for (const lead of items.data) {
			normalized.push({
				id: lead.accLeadId,
				demographics: lead.demographics,
				mayBeDemographics: lead.mayBeDemographics,
				selectedMeetingSlots: lead.selectedMeetingSlots,
				alignedCommitmentIds: items.alignedCommitments
					.filter(c => c.accLeadId === lead.accLeadId)
					.map(c => c.commitmentId) ?? [],
				alignedAccIds: items.alignedAccs
					.filter(a => a.accLeadId === lead.accLeadId)
					.map(a => a.accId) ?? []
			});
		}
	} else {
		for (const accApplication of items.data) {
			normalized.push({
				id: accApplication.accApplicationId,
				demographics: accApplication.demographics,
				mayBeDemographics: accApplication.mayBeDemographics,
				selectedMeetingSlots: accApplication.selectedMeetingSlots,
				alignedAccIds: items.alignedAccs
					.filter(a => a.accApplicationId === accApplication.accApplicationId)
					.map(a => a.accId) ?? [],
				alignedCommitmentIds: items.alignedCommitments
					.filter(a => a.accApplicationId === accApplication.accApplicationId)
					.map(c => c.commitmentId) ?? []
			});
		}
	}

	// Now process all normalized items together
	for (const item of normalized) {
		let sortingScore = 0;
		const alignedCommitmentsWithScore = item.alignedCommitmentIds.length
			? commitmentsWithScore.filter(c => item.alignedCommitmentIds.includes(c.commitmentId))
			: [];

		const scores = alignedCommitmentsWithScore.map(c => {
			const matchDifficulty = c.matchDifficulty == 'Low' ? MATCH_DIFFICULTY_LOW : c.matchDifficulty == 'High' ? MATCH_DIFFICULTY_HIGH : MATCH_DIFFICULTY_NORMAL;
			const impact = c.impact == 'Critical' ? IMPACT_CRITICAL : c.impact == 'Important' ? IMPACT_IMPORTANT : c.impact == 'Standard' ? IMPACT_STANDARD : IMPACT_INTERNAL;
			return c.urgency * 100 + matchDifficulty + impact;
		}).sort((a, b) => a > b ? 1 : -1);

		sortingScore += combineScores(scores);

		if (item.selectedMeetingSlots) {
			const accAvailableSeats = getAccAvailableSeats(util, item.alignedAccIds, accScheduleAndAvailableSeatCount, item.selectedMeetingSlots);
			sortingScore += accAvailableSeats != 0 && accAvailableSeats < 5 ? 200 : 0;
		}

		sortingScore += calculateValueScore(item.demographics, item.mayBeDemographics);

		scoreMap[item.id] = sortingScore;
	}

	return scoreMap;
}

/**
 * Combines an array of scores into a single weighted score.
 * Each subsequent score is weighted by SCORE_ADJUST.
 * @param scores Array of scores to combine.
 * @returns Combined score.
 */
function combineScores(scores: number[]) {
	if (!scores.length) return 0;

	let result = 0;
	const SCORE_ADJUST = .1;
	let m = 1;
	for (const score of scores) {
		result += score * m;
		m *= SCORE_ADJUST;
	}
	return result;
}


/**
 * Calculates the total available seats for an applicant across aligned accelerators they can attend.
 * @param util UtilityService instance for date formatting and helpers.
 * @param accIds Array of aligned accelerator IDs.
 * @param accScheduleAndAvailableSeatCount Map of accId to schedule and available seat count.
 * @param selectedMeetingSlots Applicant's selected meeting slots.
 * @returns Total available seats the applicant can attend.
 */
function getAccAvailableSeats(
	util: UtilityService,
	accIds: number[],
	accScheduleAndAvailableSeatCount: { [accId: number]: AccScheduleAvailableSeatCount },
	selectedMeetingSlots: SelectedSlots
): number {
	let accAvailableSeats = 0;
	for (const accId of accIds) {
		const accSchedule = accScheduleAndAvailableSeatCount[accId];
		if (accSchedule) {
			const { dayIndex, startTimeIndex } = util.date.formatAccMeetingSlot(accSchedule.meetingStartSlot, accSchedule.standardFourTimeZoneId);
			const canAttend = canAttendAcc(dayIndex, startTimeIndex, selectedMeetingSlots);

			if (canAttend) {
				accAvailableSeats += accSchedule.availableSeats;
			}
		}
	}
	return accAvailableSeats;
}


/**
 * Determines if an applicant can attend an accelerator meeting based on their selected slots.
 * @param dayIndex Day of the week index.
 * @param startTimeIndex Start time index for the meeting.
 * @param selectedMeetingSlots Applicant's selected meeting slots.
 * @returns True if the applicant can attend, false otherwise.
 */
export function canAttendAcc(dayIndex: WeekdayIndex, startTimeIndex: number, selectedMeetingSlots: SelectedSlots): boolean {
	const dayKey = SLOTS_DAY_MAP[dayIndex];
	return selectedMeetingSlots[dayKey].charAt(startTimeIndex) != '-';
}


/**
 * Calculates a value score based on demographics.
 * @param demographics Demographics string.
 * @returns Numeric score.
 */
export function calculateValueScore(demographics: string, mayBeDemographics: string) {
	const demographicsArr = (demographics || '').split(', ');
	const mayBeDemographicsArr = (mayBeDemographics || '').split(', ');
	let score = 0;

	if (demographicsArr.find(d => d == 'LMI')) score += LMI;
	if (demographicsArr.find(d => d == 'BIPOC')) score += BIPOC;
	else if (mayBeDemographicsArr.find(m => m == 'Maybe Bipoc')) score += MAYBE_BIPOC;

	if (demographicsArr.find(d => d == 'Female')) score += FEMALE;
	else if (mayBeDemographicsArr.find(m => m == 'Maybe/Non-male')) score += MAYBE_NONMALE;

	return score;
}